/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { CollectionComparator, EMPTY_ARRAY, sortedIndexWith } from '@gh/core-util';

import {
  TableGroupIndex,
  TableGroupIndexDriver,
  TableIndex,
  TableIndexBuilder,
} from './table.interfaces';

export function createGroupIndexDriver<T>(name: string,
                                          getId: TransformFn<T, ID>,
                                          includes: PredicateFn<T>,
                                          groupBy: TransformFn<T, any>,
                                          comparator: CollectionComparator<T>): TableGroupIndexDriver<T> {
  const create = () => ({});
  const modify = (index: TableGroupIndex<T>) => new TableGroupIndexBuilder(index);
  const getGroupedData = (index: TableGroupIndex<T>, value: any) => index[value] || EMPTY_ARRAY;

  class TableGroupIndexBuilder implements TableIndexBuilder<T> {
    private index: Hash<T[]>;
    private copied = false;
    private copiedGroup: Hash<boolean> = {};

    constructor(private originalIndex: Hash<T[]>) {

    }

    insert(row: T): void {
      if (!includes(row)) {
        return;
      }

      const groupKey = groupBy(row);

      this.copyGroup(groupKey);

      const { index } = this;

      const group = index[groupKey] || [];
      const groupIndex = sortedIndexWith(group, row, comparator);

      group.splice(groupIndex, 0, row);
    }

    update(oldRow: T, newRow: T): void {
      this.remove(oldRow);
      this.insert(newRow);
    }

    remove(row: T): void {
      if (!includes(row)) {
        return;
      }

      const groupKey = groupBy(row);

      this.copyGroup(groupKey);

      const { index } = this;

      const group = index[groupKey] || [];
      const groupIndex = sortedIndexWith(group, row, comparator);

      group.splice(groupIndex, 1);
    }

    build(): TableIndex<T> {
      return this.index || this.originalIndex;
    }

    private copy(): void {
      if (!this.copied) {
        this.index = { ...this.originalIndex };
        this.copied = true;
      }
    }

    private copyGroup(groupName: string): void {
      this.copy();

      const { copiedGroup } = this;
      if (!copiedGroup[groupName]) {
        const group = this.originalIndex[groupName];
        this.index[groupName] = group && group.concat() || [];
        this.copiedGroup[groupName] = true;
      }
    }
  }

  return {
    name,
    create,
    modify,
    getGroupedData,
  };
}
